import './App.css';
import { useLocation, useNavigate } from 'react-router-dom'; 
import Login from './Components/Login';
import CreateAccountForm from './Components/CreateAccountForm';
import { useState, useEffect } from 'react';
import { auth } from './firebase';
import { validateSession } from './services/authService';
import { onAuthStateChanged } from 'firebase/auth';

import Logout from "./Components/Logout"; 
import Dashboard from './Pages/Dashboard.jsx';
import { Route, Routes } from "react-router-dom";
import AdminSidebar from './Components/Common/AdminSidebar.jsx';
import UsersPage from './Pages/UsersPage.jsx';
import ReportPage from "./Pages/ReportPage";
import LeavePage from "./Pages/LeavePage";
import AttendancePage from "./Pages/AttendancePage";
import SuperadminSidebar from "./Components/Common/SuperAdminSidebar";
import SuperadminDashboard from "./Pages/SuperadminDashboard";

import SuperadminUsersPage from './Pages/SuperadminUsersPage.jsx';
import SuperadminReportPage from './Pages/SuperadminReportPage.jsx';
import UserSidebar from './Components/Common/UserSidebar.jsx';
import UserLeavePage from './Pages/UserLeavePage.jsx';

import ManageLeaveTypes from "./Pages/ManageLeaveTypes";

import UserAttendance from './Pages/UserAttendance.jsx';

import UserDashboard from './Pages/UserDashboard.jsx';

import CompanyDashboard from './Pages/CompanyDashboard.jsx';
import ManageRolesPage from './Pages/ManageRolesPage';
import EmailSettings from './Pages/EmailSettings.jsx';
import AccountRequestsPage from './Pages/AccountRequestsPage.jsx';

import DeviceManagement from './Components/Admin/DeviceManagement';

function App() {
  const location = useLocation(); 
  const navigate = useNavigate();

  const isAuthPage = location.pathname === '/' || location.pathname === '/register' || location.pathname === '/create-account';
  const isSuperadminDashboard = location.pathname === '/superadmin/dashboard';
  const isSuperadminPage = location.pathname.startsWith('/superadmin') && !isSuperadminDashboard;
  const isUserPage = location.pathname.startsWith('/user');

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        const token = localStorage.getItem("idToken");
        
        if (!token || !user) {
          if (!isAuthPage) {
            navigate('/');
          }
          setIsLoading(false);
          return;
        }

        const isValid = await validateSession(user);
        if (!isValid) {
          localStorage.clear();
          if (!isAuthPage) {
            navigate('/');
          }
          setIsLoading(false);
          return;
        }
        setIsLoading(false);

        // Redirect to appropriate dashboard if on login page
        if (isAuthPage) {
          const userRole = localStorage.getItem('userRole');
          if (userRole === 'superadmin') {
            navigate('/superadmin/dashboard');
          } else if (userRole === 'admin') {
            navigate('/dashboard');
          } else if (userRole === 'user') {
            navigate('/user/dashboard');
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.clear();
        if (!isAuthPage) {
          navigate('/');
        }
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, isAuthPage]);

  if (isLoading) {
    return <div className="loading">Loading...</div>; // Add appropriate loading UI
  }

  return (
    <div className="app-container">
      {/* Render background and sidebar only if not on login or register page */}
      {!isAuthPage && !isSuperadminDashboard && (
        <>
          <div className="background-wrapper">
            <div className="background-gradient" />
            <div className="background-blur" />
          </div>
          {isSuperadminPage ? (
            <SuperadminSidebar />
          ) : isUserPage ? (
            <UserSidebar />
          ) : (
            <AdminSidebar />
          )}
        </>
      )}

      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/create-account" element={<CreateAccountForm />} />
      
        <Route path="/dashboard" element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        } />
        <Route path="/employee" element={
          <RequireAuth>
            <UsersPage />
          </RequireAuth>
        } />
        <Route path="/leaves" element={
          <RequireAuth>
            <LeavePage />
          </RequireAuth>
        } />
        <Route path="/reports" element={
          <RequireAuth>
            <ReportPage />
          </RequireAuth>
        } />
        <Route path="/attendance" element={
          <RequireAuth>
            <AttendancePage />
          </RequireAuth>
        } />

        <Route path="/logout" element={<Logout />} />
       
        <Route path="/superadmin/dashboard" element={
          <RequireAuth>
            <SuperadminDashboard />
          </RequireAuth>
        } />
        <Route path="/superadmin/users" element={
          <RequireAuth>
            <SuperadminUsersPage />
          </RequireAuth>
        } />
        <Route path="/superadmin/reports" element={
          <RequireAuth>
            <SuperadminReportPage />
          </RequireAuth>
        } />     
        <Route path="/superadmin/manage-leave-types" element={
          <RequireAuth>
            <ManageLeaveTypes />
          </RequireAuth>
        } />
        <Route path="/superadmin/account-requests" element={
          <RequireAuth>
            <AccountRequestsPage />
          </RequireAuth>
        } />
        <Route path="/superadmin/CompanyDashboard" element={
          <RequireAuth>
            <CompanyDashboard />
          </RequireAuth>
        } />
        <Route path="/superadmin/company/:companyId" element={
          <RequireAuth>
            <CompanyDashboard />
          </RequireAuth>
        } />
    
    
        <Route path="/user/dashboard" element={
          <RequireAuth>
            <UserDashboard />
          </RequireAuth>
        } />
        <Route path="/user/leave" element={
          <RequireAuth>
            <UserLeavePage />
          </RequireAuth>
        } />
         <Route path="/user/attendance" element={
          <RequireAuth>
            <UserAttendance />
          </RequireAuth>
        } />
        <Route path="/manage-roles" element={
          <RequireAuth>
            <ManageRolesPage />
          </RequireAuth>
        } />
        <Route path="/email-settings" element={
          <RequireAuth>
            <EmailSettings />
          </RequireAuth>
        } />

        <Route path="/admin/device-management" element={<DeviceManagement />} />

      </Routes>
    </div>
  );
}

const RequireAuth = ({ children }) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("idToken");
        const user = auth.currentUser;

        if (!token || !user) {
          navigate('/');
          return;
        }

        const isValid = await validateSession(user);
        if (!isValid) {
          localStorage.clear();
          navigate('/');
          return;
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.clear();
        navigate('/');
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isChecking) {
    return null; // or a loading spinner
  }

  return children;
};

export default App;


























// import './App.css';
// import { useLocation } from 'react-router-dom'; 
// import Login from './Components/Login';

// import Logout from "./Components/Logout"; 
// import Dashboard from './Pages/Dashboard.jsx';
// import { Route, Routes } from "react-router-dom";
// import AdminSidebar from './Components/Common/AdminSidebar.jsx';
// import UsersPage from './Pages/UsersPage.jsx';
// import ReportPage from "./Pages/ReportPage";
// import LeavePage from "./Pages/LeavePage";
// import AttendancePage from "./Pages/AttendancePage";
// import SuperadminSidebar from "./Components/Common/SuperAdminSidebar";
// import SuperadminDashboard from "./Pages/SuperadminDashboard";

// import SuperadminUsersPage from './Pages/SuperadminUsersPage.jsx';
// import SuperadminReportPage from './Pages/SuperadminReportPage.jsx';
// import UserSidebar from './Components/Common/UserSidebar.jsx';
// import UserLeavePage from './Pages/UserLeavePage.jsx';

// import ManageLeaveTypes from "./Pages/ManageLeaveTypes";

// import UserAttendance from './Pages/UserAttendance.jsx';

// import UserDashboard from './Pages/UserDashboard.jsx';

// import CompanyDashboard from './Pages/CompanyDashboard.jsx';
// import ManageRolesPage from './Pages/ManageRolesPage';
// import EmailSettings from './Pages/EmailSettings.jsx';


// function App() {
//   const location = useLocation(); 

  
//   const isAuthPage = location.pathname === '/' || location.pathname === '/register';
//   const isSuperadminDashboard = location.pathname === '/superadmin/dashboard';
//   const isSuperadminPage = location.pathname.startsWith('/superadmin') && !isSuperadminDashboard;
//   const isUserPage = location.pathname.startsWith('/user');

//   return (
//     <div className="app-container">
//       {/* Render background and sidebar only if not on login or register page */}
//       {!isAuthPage && !isSuperadminDashboard && (
//   <>
//     <div className="background-wrapper">
//       <div className="background-gradient" />
//       <div className="background-blur" />
//     </div>
//     {isSuperadminPage ? (
//       <SuperadminSidebar />
//     ) : isUserPage ? (
//       <UserSidebar />
//     ) : (
//       <AdminSidebar />
//     )}
//   </>
// )}

//       <Routes>

//         <Route path="/" element={<Login />} />
      
//         <Route path="/dashboard" element={
//           <RequireAuth>
//             <Dashboard />
//           </RequireAuth>
//         } />
//         <Route path="/employee" element={<UsersPage />} />
//         <Route path="/leaves" element={<LeavePage />} />
//         <Route path="/reports" element={<ReportPage />} />
//         <Route path="/attendance" element={<AttendancePage />} />

//         <Route path="/logout" element={<Logout />} />
       
//         <Route path="/superadmin/dashboard" element={<SuperadminDashboard />} />
//         <Route path="/superadmin/users" element={<SuperadminUsersPage />} />
//         <Route path="/superadmin/reports" element={<SuperadminReportPage />} />     
//         <Route path="/superadmin/manage-leave-types" element={<ManageLeaveTypes />} />
//         <Route path="/superadmin/CompanyDashboard" element={<CompanyDashboard />} />
//         <Route path="/superadmin/company/:companyId" element={<CompanyDashboard />} />
    
    
//         <Route path="/user/dashboard" element={<UserDashboard />} />
//         <Route path="/user/leave" element={<UserLeavePage />} />
//          <Route path="/user/attendance" element={<UserAttendance />} />
//         <Route path="/manage-roles" element={<ManageRolesPage />} />
//         <Route path="/email-settings" element={<EmailSettings />} />

//       </Routes>
//     </div>
//   );
// }

// const RequireAuth = ({ children }) => {
//   const token = localStorage.getItem("idToken");
//   if (!token) {
//     window.location.href = "/";
//     return null;
//   }
//   return children;
// };

// export default App;
